import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AssetStatus, GalleryColorLabel, GalleryDto, GalleryFeedbackOptions } from '@picflow/types';
import { billingScopes } from '../../../billing-consts';
import { defaultColorLabels } from '../../../configs/default-color-labels';
import { Filter } from '../../../gallery-shared/interfaces/filter.interface';
import { AssetStatusColorPipe } from '../../../gallery/pipes/asset-status-color.pipe';
import { AssetStatusIconPipe } from '../../../gallery/pipes/asset-status-icon.pipe';
import { AssetStatusNamePipe } from '../../../gallery/pipes/asset-status-name.pipe';
import { AssetStatusOrderPipe } from '../../../gallery/pipes/asset-status-order.pipe';
import { GalleryAccessLevel } from '../../../lib/access-level';
import { getAllowedAndEnabledFeedbackOptions } from '../../../lib/get-allowed-feedback-options';
import { PermissionService } from '../../../services/permission.service';
import { RadioButtonComponent } from '../../../shared-extra/components/radio-button/radio-button.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';

@Component({
  selector: 'app-view-filter',
  templateUrl: './view-filter.component.html',
  styleUrls: ['./view-filter.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ContextMenuComponent,
    ButtonDirective,
    SvgComponent,
    RadioButtonComponent,
    TranslatePipe,
    AssetStatusOrderPipe,
    AssetStatusIconPipe,
    AssetStatusColorPipe,
    AssetStatusNamePipe,
  ],
})
export class ViewFilterComponent implements OnInit {
  private permissionService = inject(PermissionService);
  allFilters: { is_liked: boolean; status: AssetStatus; color_label: { [color: string]: boolean } };
  hasFilters = false;
  colorLabels: GalleryColorLabel[] = [];
  allowedAndEnabledFeedbackOptions: GalleryFeedbackOptions[];

  @Input() gallery: GalleryDto;
  @Input() accessLevel: GalleryAccessLevel;
  @Output() onFilterChange: EventEmitter<Filter[]> = new EventEmitter();

  private route = inject(ActivatedRoute);

  ngOnInit() {
    this.colorLabels = this.gallery.color_labels;
    if (!this.permissionService.canDisplayElement(billingScopes.asset.customLabels, this.gallery))
      this.colorLabels = defaultColorLabels;

    this.allowedAndEnabledFeedbackOptions = getAllowedAndEnabledFeedbackOptions(
      this.gallery,
      undefined,
      this.accessLevel
    );
    this.handleFiltersFromQuery();
  }

  private handleFiltersFromQuery() {
    this._resetFilter();
    const filterString = this.route.snapshot.queryParams.filters;
    if (filterString) {
      let filters: Filter[];
      try {
        filters = JSON.parse(filterString);
      } catch (err) {
        console.warn('Not able to parse filters from url');
        console.error(err);
      }
      if (filters) {
        const likedFilter = filters.find(f => f.key === 'is_liked');
        const statusFilter = filters.find(f => f.key === 'status');
        const colorLabelFilter = filters.find(f => f.key === 'color_label');
        if (likedFilter) {
          this.allFilters.is_liked = <boolean>likedFilter.values[0];
        }
        if (statusFilter) {
          this.allFilters.status = <AssetStatus>statusFilter.values[0];
        }
        if (colorLabelFilter) {
          colorLabelFilter.values.forEach(value => {
            this.allFilters.color_label[<string>value] = true;
          });
        }
      }
    }
  }

  toggleFilter(filter: string, value: boolean | string) {
    if (filter === 'color_label') {
      this.allFilters.color_label[<string>value] = !this.allFilters.color_label[<string>value];
    } else {
      if (this.allFilters[filter] === value) {
        this.allFilters[filter] = undefined;
      } else {
        this.allFilters[filter] = value;
      }
    }
    this.calcFilters();
  }

  private _resetFilter() {
    this.allFilters = {
      is_liked: undefined,
      status: undefined,
      color_label: { null: false, red: false, orange: false, blue: false, purple: false, green: false },
    };
  }

  resetFilter() {
    this._resetFilter();
    this.calcFilters();
  }

  private calcFilters() {
    const filters = [];
    if (this.allFilters.is_liked !== undefined) {
      filters.push({
        key: 'is_liked',
        values: [this.allFilters.is_liked],
      });
    }
    if (this.allFilters.status !== undefined) {
      filters.push({
        key: 'status',
        values: [this.allFilters.status],
      });
    }

    const labels = [];
    Object.keys(this.allFilters.color_label).forEach(label => {
      const value = this.allFilters.color_label[label];
      if (value) labels.push(label);
    });
    if (labels.length) {
      filters.push({
        key: 'color_label',
        values: labels,
      });
    }

    this.hasFilters = !!filters.length;
    this.onFilterChange.emit(filters);
  }
}
